/**
 * THE customer configurator's state machine — seeding, snapping, and the
 * split into the reserved keys vs. ``extra_config``.
 *
 * ``CustomerConfiguratorForm`` renders an operation's declared fields; it
 * does not decide what a freshly opened form STARTS on, nor what happens
 * when the customer switches to a material that prices different values.
 * That lived inside ``DynamicPrimaryConfigurator`` — fine while the buyer's
 * quote page was the only surface that priced a part, and wrong the moment
 * a second surface had to produce the same price for the same file.
 *
 * It is extracted rather than copied on purpose. The seeding rules are the
 * difference between "the admin's sample" and "what the buyer would pay":
 * an unseeded bound option, an axis value the material does not price, or a
 * tunable left at nothing all change the number. Two implementations of
 * these rules are two prices.
 *
 * Callers:
 *   - ``DynamicPrimaryConfigurator`` — the buyer's configure→price→add card,
 *   - ``SampleConfiguration`` — the onboarding price page's sample parts.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { useProcessVariantAxis } from "@/api/queries/processes";
import {
  tunablesFromFeasibility,
  useFeasibilityPresets,
  type TunablePreset,
} from "@/api/queries/pricing";
import { optionPropertyOf } from "@/lib/boundColor";
import { offeredAxisValues as offeredAxisValuesFor } from "@/lib/offeredAxisValues";

import type { AnalysisSnapshot, Catalog } from "@/api/types";

/** Line-identity keys — everything else rides ``extra_config``. */
const RESERVED_KEYS = new Set(["material_code", "quantity", "customer_note"]);

/** Best-effort part dimensions for the feasibility query — handles the DXF
 *  flat ``[xmin,ymin,xmax,ymax]`` bbox and the mesh ``{size:[x,y,z]}`` shape. */
export function dimsFromAnalysis(a: AnalysisSnapshot | null | undefined): {
  dim_x_mm?: number;
  dim_y_mm?: number;
  dim_z_mm?: number;
} {
  if (!a) return {};
  const bbox = a.bounding_box as unknown;
  if (Array.isArray(bbox) && bbox.length >= 4) {
    return { dim_x_mm: bbox[2] - bbox[0], dim_y_mm: bbox[3] - bbox[1] };
  }
  const size = (bbox as { size?: unknown } | null)?.size;
  if (Array.isArray(size) && size.length >= 3) {
    return {
      dim_x_mm: Number(size[0]),
      dim_y_mm: Number(size[1]),
      dim_z_mm: Number(size[2]),
    };
  }
  return {};
}

/** Extract per-field ``default`` values declared in the configurator schema
 *  so fields the customer never touches still send their declared value to
 *  ``/pricing/line`` and into the persisted cart line. Inert for schemas
 *  whose fields declare no ``default`` (e.g. laser): returns ``{}``. */
export function defaultsFromFields(
  schema: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!schema || typeof schema !== "object") return {};
  const fields = (schema as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) return {};
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const key = String((field as { key?: unknown })?.key ?? "");
    const value = (field as { default?: unknown })?.default;
    if (key && value !== undefined) out[key] = value;
  }
  return out;
}

/** KAN-1557 — material slots declaring ``default_line_material``: they start
 *  on the part's own material (a support printed in the part's plastic) when
 *  they offer it, and keep their authored ``default`` otherwise.
 *
 *  KAN-1945 — a slot may also name an analysis output in
 *  ``default_line_material_if``: it follows the part's material only when
 *  that fact is positive, so a part without overhangs keeps „no supports".
 *  The key is the admin's data; an unknown fact keeps the old behaviour. */
export function lineMaterialSlotDefaults(
  schema: Record<string, unknown> | null | undefined,
  materialCode: string,
  analysis?: object | null,
): Record<string, unknown> {
  const fields = (schema as { fields?: unknown } | null | undefined)?.fields;
  if (!Array.isArray(fields) || !materialCode) return {};
  const out: Record<string, unknown> = {};
  for (const field of fields as Array<{
    key?: unknown;
    default_line_material?: unknown;
    default_line_material_if?: unknown;
    enum_values?: unknown;
  }>) {
    const key = String(field.key ?? "");
    if (!key || field.default_line_material !== true) continue;
    const factKey = field.default_line_material_if;
    if (typeof factKey === "string" && factKey && analysis) {
      const fact = (analysis as Record<string, unknown>)[factKey];
      if (typeof fact === "number" && fact <= 0) continue;
    }
    const offered = Array.isArray(field.enum_values)
      ? field.enum_values.map((v) => String(v))
      : [];
    const match = offered.find(
      (v) => v.toLowerCase() === materialCode.toLowerCase(),
    );
    if (match !== undefined) out[key] = match;
  }
  return out;
}

/** Schema defaults plus the reserved keys (``material_code``/``quantity``) a
 *  freshly opened configurator starts on — the first catalog material for the
 *  given process and a quantity of 1. */
export function seedCustomerConfiguratorValues(
  schema: Record<string, unknown> | null | undefined,
  catalog: Catalog | null | undefined,
  processType: string,
  analysis?: object | null,
): Record<string, unknown> {
  const materials = (catalog?.materials ?? []).filter((material) =>
    (material.process_types ?? []).includes(processType),
  );
  const materialCode = materials[0]?.code ?? "";
  return {
    ...defaultsFromFields(schema),
    ...lineMaterialSlotDefaults(schema, materialCode, analysis),
    material_code: materialCode,
    quantity: 1,
  };
}

export interface CustomerConfiguratorState {
  value: Record<string, unknown>;
  setValue: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  /** Catalog materials this process can be made from. */
  materials: Catalog["materials"];
  materialCode: string;
  quantity: number;
  /** Every non-reserved field — what rides ``extra_config`` AND, byte for
   *  byte, the line's ``dimensions``. One object, so the previewed price
   *  cannot drift from the persisted one. */
  extraConfig: Record<string, unknown>;
  customerNote: string | undefined;
  axisKey: string | undefined;
  axisUnit: string | null;
  /** The declared axis has no usable value yet — pricing would 422. */
  axisUnresolved: boolean;
  tunables: Record<string, TunablePreset>;
  /** Volby dílny se ještě načítají a dosazují — cena na ně čeká (KAN-1642). */
  tunablesSeeding: boolean;
  /** KAN-2084 — žádný schopný stroj obchodu díl s touto konfigurací nevezme
   *  (``/pricing/feasibility`` → ``orderable: false``, např. hodnota osy nad
   *  limitem všech strojů, i po materiálu). Přidat do košíku nejde. */
  noMachineFits: boolean;
}

export function useCustomerConfiguratorState({
  schema,
  catalog,
  processType,
  analysis,
  initialValue,
}: {
  schema: Record<string, unknown> | null | undefined;
  catalog: Catalog | null | undefined;
  processType: string;
  analysis: AnalysisSnapshot | null | undefined;
  /** Extra seed merged OVER the schema defaults (a saved line being re-edited). */
  initialValue?: Record<string, unknown>;
}): CustomerConfiguratorState {
  const materials = useMemo(
    () =>
      (catalog?.materials ?? []).filter((m) =>
        (m.process_types ?? []).includes(processType),
      ),
    [catalog, processType],
  );

  const [value, setValue] = useState<Record<string, unknown>>(() => ({
    ...seedCustomerConfiguratorValues(schema, catalog, processType, analysis),
    ...(initialValue ?? {}),
  }));

  const materialCode =
    typeof value.material_code === "string" ? value.material_code : "";
  const quantity = Number(value.quantity) || 1;

  // KAN-1557 — a slot that starts on the part's material FOLLOWS it while the
  // customer has not picked something else: switching the part from PLA to
  // PETG moves a support that was "the same as the part" along with it.
  const previousMaterial = useRef(materialCode);
  useEffect(() => {
    const before = previousMaterial.current;
    previousMaterial.current = materialCode;
    if (before === materialCode) return;
    const follow = lineMaterialSlotDefaults(schema, materialCode, analysis);
    // Before any material was known (catalog still loading) the slot sat on
    // its authored fallback — that is not a customer's pick either.
    const fallback = before === "" ? defaultsFromFields(schema) : {};
    setValue((prev) => {
      const seed: Record<string, unknown> = {};
      for (const [key, next] of Object.entries(follow)) {
        const current = prev[key];
        const untouched =
          current === undefined ||
          (key in fallback && current === fallback[key]) ||
          (typeof current === "string" &&
            current.toLowerCase() === before.toLowerCase());
        if (untouched && current !== next) seed[key] = next;
      }
      return Object.keys(seed).length > 0 ? { ...prev, ...seed } : prev;
    });
  }, [schema, materialCode, analysis]);

  // The material is seeded once and never revisited, so one the tenant later
  // removes from the price list — or one filtered out when the catalog is
  // refetched for a different process — would stay selected forever, and the
  // preview would 422 with no way out. Snap only when the current pick is
  // genuinely gone; an empty catalog is the separately-reported "no priced
  // materials" state and is left alone.
  useEffect(() => {
    if (materials.length === 0) return;
    setValue((prev) => {
      const current =
        typeof prev.material_code === "string" ? prev.material_code : "";
      if (materials.some((m) => m.code === current)) return prev;
      return { ...prev, material_code: materials[0].code };
    });
  }, [materials]);

  // Pre-select each bound-choice field (e.g. "Barva") to the SELECTED
  // material's FIRST offered option. Options are material-dependent
  // (``material.option_sources``), not a static ``field.default``, so this
  // cannot live in ``defaultsFromFields``. Without it the axis is absent on
  // the untouched fast path and the cart 422s ``line_dimensions_missing``.
  useEffect(() => {
    const fields = (schema as { fields?: unknown } | null | undefined)?.fields;
    if (!Array.isArray(fields)) return;
    const material = materials.find((m) => m.code === materialCode);
    if (!material) return;
    setValue((prev) => {
      const seed: Record<string, unknown> = {};
      for (const field of fields as Array<{
        key?: unknown;
        options_source?: unknown;
      }>) {
        const property = optionPropertyOf(field);
        const key = String(field.key ?? "");
        if (!property || !key) continue;
        const options = material.option_sources?.[property] ?? [];
        if (options.length === 0) continue;
        const current = prev[key];
        const alreadyValid =
          typeof current === "string" && options.some((o) => o.code === current);
        if (!alreadyValid) seed[key] = options[0].code;
      }
      return Object.keys(seed).length > 0 ? { ...prev, ...seed } : prev;
    });
  }, [schema, materials, materialCode]);

  const customerNote =
    typeof value.customer_note === "string" ? value.customer_note : undefined;
  const extraConfig = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (!RESERVED_KEYS.has(k)) out[k] = v;
    }
    return out;
  }, [value]);

  const manifestQuery = useProcessVariantAxis(processType);
  const variantParam = manifestQuery.data?.variant_param;
  const axisKey =
    variantParam && variantParam.type !== "none" && variantParam.key
      ? variantParam.key
      : undefined;
  const axisUnit = variantParam?.unit ?? null;
  const axisValue = axisKey ? Number(value[axisKey]) || 0 : 0;

  // The closed set of axis values the SELECTED material is priced for. Empty
  // whenever there is nothing to pick from — the form renders a free input then.
  // KAN-2190 — nabídka TÉTO operace, ne sloučená přes všechny operace materiálu.
  const offeredAxisValues = useMemo(
    () =>
      offeredAxisValuesFor(
        materials.find((m) => m.code === materialCode),
        processType,
        axisKey,
      ),
    [axisKey, materials, materialCode, processType],
  );

  // Keep the axis pick inside the closed set the SELECTED material offers: a
  // value the previous material priced is usually absent from the next one's
  // list, and leaving it there means a form showing a value nobody can produce.
  // A still-offered value is never touched, so the customer's own pick — and a
  // re-edited line's saved one — survives.
  useEffect(() => {
    if (!axisKey || offeredAxisValues.length === 0) return;
    setValue((prev) => {
      const current = prev[axisKey];
      if (typeof current === "number" && offeredAxisValues.includes(current)) {
        return prev;
      }
      return { ...prev, [axisKey]: offeredAxisValues[0] };
    });
  }, [axisKey, offeredAxisValues]);

  // ``isLoading`` and NOT ``isPending``: a disabled react-query v5 query —
  // which is what an absent process_type produces — is pending for good. Both
  // escape hatches out of ``axisUnresolved`` are "there is nothing to know"
  // rather than "not known yet", so this cannot deadlock.
  const axisPick = axisKey ? value[axisKey] : undefined;
  const axisUnresolved =
    manifestQuery.isLoading ||
    (offeredAxisValues.length > 0 &&
      !(typeof axisPick === "number" && offeredAxisValues.includes(axisPick)));

  // Tenant-tunable B2C options (presets/slider) for this part, merged across
  // capable machines. Each tunable owns its config key (== pricing key).
  const dims = useMemo(() => dimsFromAnalysis(analysis), [analysis]);
  const feasibility = useFeasibilityPresets(
    materialCode
      ? {
          process_type: processType,
          dim_x_mm: dims.dim_x_mm ?? null,
          dim_y_mm: dims.dim_y_mm ?? null,
          dim_z_mm: dims.dim_z_mm ?? null,
          material_code: materialCode,
          // The variant axis is an ordinary part fact. Deliberately NOT the
          // whole dimensions dict: presets depend on machine capability, not
          // on the tunable values this very query seeds.
          inputs: axisKey && axisValue > 0 ? { [axisKey]: axisValue } : {},
        }
      : null,
  );
  const tunables = useMemo(
    () => tunablesFromFeasibility(feasibility.data),
    [feasibility.data],
  );
  const tunableKeys = useMemo(() => Object.keys(tunables), [tunables]);
  // KAN-1642 — dokud se neví, které volby dílna nabízí, nejsou výchozí
  // hodnoty dosazené a každé dosazení by pustilo nový výpočet ceny. Cena na
  // to počká a nacení až ustálený formulář.
  const tunablesSeeding =
    materialCode !== "" && (feasibility.isSettling || feasibility.isFetching);

  // Seed each tunable's default into config once its presets load (and clamp /
  // re-pick when the current value isn't offered by the tenant). Idempotent —
  // returns the same object when nothing needs seeding, so it can't loop.
  useEffect(() => {
    if (tunableKeys.length === 0) return;
    setValue((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of tunableKeys) {
        const tunable = tunables[key];
        const values = tunable.presets
          .map((p) => p.value)
          .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
        if (values.length === 0) continue;
        const cur = next[key];
        if (tunable.allow_slider) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          if (typeof cur !== "number" || cur < min || cur > max) {
            next[key] =
              typeof tunable.default === "number"
                ? Math.min(max, Math.max(min, tunable.default))
                : min;
            changed = true;
          }
        } else if (typeof cur !== "number" || !values.includes(cur)) {
          next[key] =
            typeof tunable.default === "number" && values.includes(tunable.default)
              ? tunable.default
              : values[0];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tunables, tunableKeys]);

  return {
    value,
    setValue,
    materials,
    materialCode,
    quantity,
    extraConfig,
    customerNote,
    axisKey,
    axisUnit,
    axisUnresolved,
    tunables,
    tunablesSeeding,
    noMachineFits: feasibility.data?.orderable === false,
  };
}
